import { Link } from "@tanstack/react-router";
import { ClipboardList, ExternalLink, Star } from "lucide-react";
import { useState } from "react";
import { PersonAdHocFeedback } from "@/components/reviews/PersonAdHocFeedback";
import { PersonReviewTrend } from "@/components/reviews/PersonReviewTrend";
import { DetailSection } from "@/components/shared/detail-section";
import { EmptyState } from "@/components/shared/empty-state";
import { ErrorState } from "@/components/shared/error-state";
import { StatusPill, type StatusTone } from "@/components/shared/status-badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { type PersonReviewHistoryEntry, usePersonReviewHistory } from "@/hooks/use-review-report";
import {
  isManagerNoteSectionBlank,
  MANAGER_NOTE_SECTIONS,
} from "@/lib/reviews/manager-note-sections";

const STATUS_TONES: Record<string, StatusTone> = {
  open: "neutral",
  complete: "info",
  closed: "success",
};

/**
 * Every review a person has ever had, in one place — EPIC-070 (WI-259,
 * WI-260, WI-263). A condensed, read-only rollup: the ratings, comments,
 * endorsements and finalized manager notes are right here, but the controls
 * that only make sense on a LIVE round (sign-off, excluding a reviewer,
 * drafting a note) stay on the review's own report page — reached from here
 * by "Open full report," never duplicated.
 *
 * Built entirely from `getReviewReport`'s existing payload per review —
 * `reviewAudienceFor` already decided, per review, how much of it this
 * viewer may see (and whether to include it at all), so there is nothing
 * left to redact here.
 */
export function PersonReviewHistory({ personId }: { personId: string }) {
  const { data, isLoading, isError, error, refetch } = usePersonReviewHistory(personId, true);

  if (isError) {
    return (
      <ErrorState
        message={error instanceof Error ? error.message : "Couldn't load review history"}
        onRetry={() => refetch()}
      />
    );
  }
  if (isLoading) return <Skeleton className="h-64 w-full" />;

  const entries = data ?? [];
  if (entries.length === 0) {
    return (
      <div className="space-y-6">
        {/* Standalone feedback exists independent of any cycle (EPIC-066/067),
            so it still has somewhere to show up even with no formal history. */}
        <PersonAdHocFeedback personId={personId} />
        <EmptyState
          icon={ClipboardList}
          title="No review history yet"
          description="Nothing to show until a review cycle has included this person."
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PersonAdHocFeedback personId={personId} />
      {/* Renders nothing until there are two cycles with something to compare. */}
      <PersonReviewTrend entries={entries} />
      <ReviewHistoryPicker entries={entries} />
    </div>
  );
}

/**
 * One round at a time, chosen from a list.
 *
 * Every round used to render stacked, so a person with five of them handed
 * you a page you could only scroll — and the round you wanted was rarely the
 * one in view. The rounds arrive newest-first, so the default selection is
 * the most recent.
 */
/**
 * The "show me everything" option. A literal rather than a real id — review
 * ids are UUIDs, so it can never collide with one.
 */
const ALL_ROUNDS = "__all__";

function ReviewHistoryPicker({ entries }: { entries: PersonReviewHistoryEntry[] }) {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const showAll = selectedId === ALL_ROUNDS;
  // Resolved rather than stored, so a selection that disappears (the list
  // refetched, the round was removed) falls back instead of blanking the panel.
  const selected = entries.find((e) => e.reviewId === selectedId) ?? entries[0];
  if (!selected) return null;

  const shown = showAll ? entries : [selected];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <label className="font-medium text-sm" htmlFor="review-history-round">
          Round
        </label>
        <Select value={showAll ? ALL_ROUNDS : selected.reviewId} onValueChange={setSelectedId}>
          <SelectTrigger className="w-full sm:w-96" id="review-history-round">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_ROUNDS}>All rounds ({entries.length})</SelectItem>
            {entries.map((entry) => (
              <SelectItem key={entry.reviewId} value={entry.reviewId}>
                {entry.cycleName} · {entry.periodStart} – {entry.periodEnd}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {!showAll && (
          <span className="text-muted-foreground text-xs">
            {entries.length} {entries.length === 1 ? "round" : "rounds"}
          </span>
        )}
      </div>
      <div className="space-y-6">
        {shown.map((entry) => (
          <ReviewHistoryCard key={entry.reviewId} entry={entry} />
        ))}
      </div>
    </div>
  );
}

function ReviewHistoryCard({ entry }: { entry: PersonReviewHistoryEntry }) {
  const { report } = entry;
  const hasContent =
    report.criteria.some((c) => c.answers.length > 0 || c.groups.length > 0) ||
    report.endorsements.length > 0 ||
    report.feedback.length > 0 ||
    report.managerNotes.length > 0;

  return (
    <DetailSection
      title={entry.cycleName}
      headerActions={
        <div className="flex items-center gap-2">
          <StatusPill
            tone={STATUS_TONES[entry.cycleStatusKey] ?? "neutral"}
            label={entry.cycleStatusKey}
          />
          <Link
            to="/dashboard/reviews/reports/$reviewId"
            params={{ reviewId: entry.reviewId }}
            className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md border border-border px-3 py-1.5 font-medium text-xs hover:bg-muted"
          >
            <ExternalLink className="size-3.5" aria-hidden />
            Open full report
          </Link>
        </div>
      }
    >
      <p className="mb-4 text-muted-foreground text-sm">
        {report.roleName}
        {report.roleLevelName ? ` · ${report.roleLevelName}` : ""} · covering {report.periodStart} –{" "}
        {report.periodEnd}
      </p>

      {!hasContent ? (
        <p className="text-muted-foreground text-sm">Nothing handed in yet.</p>
      ) : (
        <div className="space-y-4">
          {report.criteria
            .filter((c) => c.answers.length > 0 || c.groups.length > 0)
            .map((criterion) => (
              <div key={criterion.questionId}>
                <p className="font-medium text-sm">{criterion.name}</p>
                <ul className="mt-1 space-y-1">
                  {criterion.answers.map((answer, index) => (
                    <li
                      key={answer.reviewerId ?? `answer-${index}`}
                      className="text-muted-foreground text-sm"
                    >
                      <span className="font-medium text-foreground">
                        {answer.relationshipName ?? "Rating"}
                        {answer.reviewerName ? ` · ${answer.reviewerName}` : ""}:
                      </span>{" "}
                      {answer.rungName ?? "Not rated"}
                      {answer.comment ? ` — ${answer.comment}` : ""}
                    </li>
                  ))}
                  {criterion.groups.map((group) => (
                    <li key={group.relationshipKey} className="text-muted-foreground text-sm">
                      <span className="font-medium text-foreground">
                        {group.relationshipName} · {group.count}{" "}
                        {group.count === 1 ? "person" : "people"}:
                      </span>{" "}
                      {group.averageOrdinal === null
                        ? "Not rated"
                        : `${group.averageOrdinal.toFixed(1)} average`}
                    </li>
                  ))}
                </ul>
              </div>
            ))}

          {report.endorsements.length > 0 && (
            <div>
              <p className="font-medium text-sm">Endorsements</p>
              <ul className="mt-1 space-y-1">
                {report.endorsements.map((note) => (
                  <li key={note.id} className="flex items-start gap-1.5 text-sm">
                    <Star className="mt-0.5 size-3.5 shrink-0 text-muted-foreground" aria-hidden />
                    <span>
                      <span className="font-medium">{note.title}</span> — {note.detail}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {report.feedback.length > 0 && (
            <div>
              <p className="font-medium text-sm">Written feedback</p>
              <ul className="mt-1 space-y-1">
                {report.feedback.map((note) => (
                  <li key={note.id} className="text-sm">
                    <span className="text-muted-foreground text-xs">
                      {note.isAnonymous
                        ? "Unsigned"
                        : (note.authorName ?? note.relationshipName ?? "Reviewer")}
                      {" · "}
                      {note.sentimentName}
                    </span>
                    <p>{note.body}</p>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {report.managerNotes.length > 0 && (
            <div>
              <p className="font-medium text-sm">Summary and Next Steps</p>
              <ul className="mt-1 space-y-2">
                {report.managerNotes.map((note) => (
                  <li key={note.id} className="text-sm">
                    <span className="text-muted-foreground text-xs">
                      {note.authorName}
                      {note.isDraft ? " · draft" : ""}
                    </span>
                    {MANAGER_NOTE_SECTIONS.filter(
                      (section) => !isManagerNoteSectionBlank(note[section.key]),
                    ).map((section) => (
                      <div key={section.key} className="mt-1">
                        <p className="font-medium text-muted-foreground text-xs">{section.label}</p>
                        <p className="whitespace-pre-line">{note[section.key]}</p>
                      </div>
                    ))}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </DetailSection>
  );
}
